// ═══════════════════════════════════════════════════════════════════════════
// La ficha de UNA vinícola, desde plataforma
//
// El operador: "en este momento la vista está mezclada".
//
// Lo estaba en los dos sentidos. Las acciones de plataforma —entrar como
// alguien— vivían dentro de la pantalla que administra una vinícola, y la
// pantalla de plataforma era una lista plana sin dónde operar sobre una en
// concreto.
//
// Aquí se separa: la lista dice CUÁLES hay, esta ficha es todo lo que se puede
// hacer con UNA. Cuatro cosas, y en este orden porque es el del trabajo real:
//
//   Equipo    → quién hay, invitar a cualquier rol, entrar como alguien
//   Timbrado  → sus datos fiscales, su organización de Facturapi, su llave
//   Datos     → nombre, serie, plan
//   Baja      → apagarla, y volver a encenderla
//
// ── LO QUE ESTA PANTALLA SIGUE SIN PODER VER ───────────────────────────────
//
// Nada de los datos de la vinícola: ni un cliente, ni una remisión, ni un peso.
// El superusuario no tiene membresía y la RLS le devuelve cero filas de todo
// (D12). Para ver lo que ve alguien hay que ENTRAR COMO ese alguien, y eso deja
// renglón con quién fue y en la piel de quién.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../auth/sesion.js";
import { entrarComo } from "../auth/suplantar.js";
import { ligaDeTicket, mensajeDeInvitacion } from "../auth/ticket.js";
import { qrComoImagen } from "../dominio/qr.js";
import { rotarLlaveFiscal } from "../fiscal/timbrado-servidor.js";
import { configSupabase } from "../auth/config.js";
import { SAT_REGIMEN_FISCAL } from "../fiscal/catalogos-sat.js";
import { Card, Label } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";

const ROLES = [
  ["owner", "Propietario", "Todo, incluida la zona de peligro."],
  ["manager", "Gerente", "Todo lo operativo, incluidas cancelaciones."],
  ["accountant", "Contabilidad", "Timbra y cancela CFDI, registra pagos."],
  ["agent", "Vendedor", "Su cartera, sus remisiones. No timbra ni cancela."],
];

export function PlataformaVinicola({ v, fiscal, planes, onCambio, onCerrar }) {
  const [seccion, setSeccion] = useState("equipo");
  const [equipo, setEquipo] = useState([]);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [ocupado, setOcupado] = useState("");
  const [ticket, setTicket] = useState(null);

  const cargarEquipo = useCallback(async () => {
    const { data, error: e } = await supabase.rpc("equipo_de_plataforma", { p_tenant: v.tenant_id });
    if (e) setError(e.message); else setEquipo(data || []);
  }, [v.tenant_id]);
  useEffect(() => { cargarEquipo(); }, [cargarEquipo]);

  const correr = async (etiqueta, fn) => {
    setOcupado(etiqueta); setError(""); setAviso("");
    try { await fn(); } catch (e) { setError(e.message); }
    setOcupado("");
  };
  const rpc = async (nombre, args, alListo) => {
    const { data, error: e } = await supabase.rpc(nombre, args);
    if (e) throw new Error(e.message);
    alListo?.(data);
    onCambio?.();
  };

  const F = fiscal || {};

  return (
    <Card style={{ borderColor: "#2a2a3a", background: "#0c0c10" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap",
                    marginBottom: 14 }}>
        <span style={{ fontSize: 16, color: "#e8e0d4" }}>{v.nombre}</span>
        <span style={{ fontSize: 11, color: "#555" }}>{v.slug}</span>
        {!v.activa && (
          <span style={{ fontSize: 10, padding: "3px 9px", borderRadius: 4,
                         background: "#1f0d0d", color: "#f87171" }}>DADA DE BAJA</span>
        )}
        <button onClick={onCerrar}
          style={{ ...btnSecondary, marginLeft: "auto", padding: "6px 14px", fontSize: 11 }}>
          Cerrar
        </button>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[["equipo", "Equipo"], ["timbrado", "Timbrado"], ["datos", "Datos y plan"],
          ["baja", v.activa ? "Dar de baja" : "Reactivar"]].map(([id, et]) => (
          <button key={id} onClick={() => { setSeccion(id); setError(""); setAviso(""); }}
            style={{ fontSize: 11, padding: "6px 13px", borderRadius: 6, cursor: "pointer",
                     fontFamily: "inherit",
                     background: seccion === id ? "#1a1a2a" : "transparent",
                     border: `1px solid ${seccion === id ? "#3a3a5a" : "#222"}`,
                     color: seccion === id ? (id === "baja" ? "#f87171" : "#a0a0c9") : "#666" }}>
            {et}
          </button>
        ))}
      </div>

      {error && <Aviso tono="mal">{error}</Aviso>}
      {aviso && <Aviso tono="bien">{aviso}</Aviso>}

      {/* ── El ticket recién emitido ───────────────────────────────────────
          Con QR, que es lo que pidió el operador: "generar un código QR para
          firmar al dueño o administrador de cualquier vinícola". */}
      {ticket && (
        <div style={{ padding: 14, background: "#101408", border: "1px solid #2a3a1a",
                      borderRadius: 8, marginBottom: 14, textAlign: "center" }}>
          <div style={{ fontSize: 11, color: "#a8d060", letterSpacing: 1, marginBottom: 8 }}>
            🎫 TICKET DE {String(ticket.role).toUpperCase()} · {v.nombre.toUpperCase()}
          </div>
          <img src={qrComoImagen(ligaDeTicket(ticket.codigo), { modulo: 6, margen: 4 })}
            alt={`QR de la invitación ${ticket.codigo}`}
            style={{ width: 190, height: 190, borderRadius: 8, imageRendering: "pixelated" }} />
          <div style={{ fontSize: 11, color: "#7a8a5a", lineHeight: 1.7, marginTop: 6 }}>
            Que lo apunte con su teléfono. Entra sin correo ni contraseña.
            <br />Una sola vez · vence el {ticket.vence}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "center" }}>
            <button onClick={() => navigator.clipboard?.writeText(
                mensajeDeInvitacion(ticket.codigo, { vinicola: v.nombre, expira: ticket.vence }))}
              style={{ ...btnSecondary, padding: "7px 14px", fontSize: 11,
                       borderColor: "#3a5a1a", color: "#a8d060" }}>
              💬 Copiar mensaje
            </button>
            <button onClick={() => setTicket(null)}
              style={{ ...btnSecondary, padding: "7px 14px", fontSize: 11 }}>
              Cerrar
            </button>
          </div>
          <div style={{ fontSize: 10, color: "#666", marginTop: 8 }}>
            Cópialo ahora: al cerrar no se vuelve a mostrar.
          </div>
        </div>
      )}

      {seccion === "equipo" && (
        <div>
          <div style={{ fontSize: 11, color: "#666", lineHeight: 1.7, marginBottom: 12 }}>
            Su pool de roles. <b style={{ color: "#888" }}>Entrar como</b> abre una
            sesión de verdad de esa persona —ves sus datos, no los tuyos— y todo lo
            que hagas queda registrado a tu nombre.
          </div>

          {equipo.map(p => (
            <div key={p.membership_id}
              style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
                       padding: "9px 0", borderBottom: "1px solid #161616",
                       opacity: p.activo ? 1 : 0.5 }}>
              <span style={{ fontSize: 13, color: "#e8e0d4", flex: 1, minWidth: 160 }}>
                {p.nombre || (p.es_dispositivo
                  ? <span style={{ color: "#8a8266" }}>Sin nombre todavía</span>
                  : p.correo)}
                <span style={{ fontSize: 10, color: "#555", display: "block" }}>
                  {p.es_dispositivo ? "entró con ticket · sin correo" : p.correo}
                  {p.tiene_llave ? " · con llave de rescate" : ""}
                </span>
              </span>
              <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4,
                             background: "#1a1a1a", color: "#c9a96e" }}>{p.role}</span>
              {p.activo && (
                <button
                  onClick={() => correr("ver:" + p.user_id, async () => {
                    if (!confirm(
                      `Vas a ver la app como ${p.nombre || p.correo} en ${v.nombre}.\n\n` +
                      `Verás sus pantallas y SUS datos. Lo que hagas queda registrado a ` +
                      `tu nombre, no al suyo.`)) return;
                    await entrarComo(v.tenant_id, p.user_id, p.nombre || p.correo);
                    window.location.reload();
                  })}
                  disabled={!!ocupado}
                  style={{ ...btnSecondary, padding: "5px 11px", fontSize: 11,
                           borderColor: "#3a3320", color: "#c9a96e",
                           opacity: ocupado ? 0.5 : 1 }}>
                  {ocupado === "ver:" + p.user_id ? "Entrando…" : "👁 Entrar como"}
                </button>
              )}
            </div>
          ))}
          {equipo.length === 0 && (
            <div style={{ fontSize: 12, color: "#666", padding: "10px 0" }}>
              Todavía no hay nadie. Emite un ticket de propietario aquí abajo.
            </div>
          )}

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #1e1e1e" }}>
            <Label>Emitir un ticket de admisión</Label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
              {ROLES.map(([r, et, ayuda]) => (
                <button key={r} title={ayuda} disabled={!!ocupado}
                  onClick={() => correr("inv:" + r, () =>
                    rpc("invitar_desde_plataforma",
                        { p_tenant: v.tenant_id, p_role: r, p_dias: 14 },
                        (d) => setTicket(d)))}
                  style={{ ...btnSecondary, padding: "8px 14px", fontSize: 12,
                           opacity: ocupado ? 0.5 : 1 }}>
                  {ocupado === "inv:" + r ? "…" : `+ ${et}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {seccion === "timbrado" && (
        <SeccionTimbrado v={v} F={F} correr={correr} ocupado={ocupado}
          setAviso={setAviso} onCambio={onCambio} />
      )}

      {seccion === "datos" && (
        <SeccionDatos v={v} planes={planes} correr={correr} ocupado={ocupado}
          rpc={rpc} setAviso={setAviso} onCambio={onCambio} />
      )}

      {seccion === "baja" && (
        <SeccionBaja v={v} correr={correr} ocupado={ocupado} rpc={rpc} setAviso={setAviso} />
      )}
    </Card>
  );
}

// ── Timbrado ───────────────────────────────────────────────────────────────
function SeccionTimbrado({ v, F, correr, ocupado, setAviso, onCambio }) {
  const [llave, setLlave] = useState("");
  const [esLive, setEsLive] = useState(false);

  const crearOrganizacion = () => correr("org", async () => {
    const { url } = configSupabase();
    const { data: s } = await supabase.auth.getSession();
    const r = await fetch(`${url}/functions/v1/organizacion-facturapi`, {
      method: "POST",
      headers: { Authorization: `Bearer ${s.session.access_token}`,
                 "Content-Type": "application/json" },
      body: JSON.stringify({ tenant_id: v.tenant_id }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || "Facturapi no aceptó la organización");
    setAviso(d.llave_de_prueba_guardada
      ? "Organización creada y su llave de PRUEBAS guardada. Ya puede timbrar de mentiras."
      : "Organización creada, pero no se pudo guardar su llave de pruebas.");
    onCambio?.();
  });

  return (
    <div style={{ fontSize: 12, color: "#888", lineHeight: 1.8 }}>
      <Renglon ok={!!F.rfc} titulo="RFC" valor={F.rfc} />
      <Renglon ok={!!F.regimen_fiscal} titulo="Régimen fiscal"
        valor={F.regimen_fiscal
          ? `${F.regimen_fiscal} · ${SAT_REGIMEN_FISCAL.find(r => r.code === F.regimen_fiscal)?.label ?? ""}`
          : ""} />
      <Renglon ok={!!F.cp} titulo="Código postal fiscal" valor={F.cp} />
      <Renglon ok={!!v.org_facturapi} titulo="Organización en Facturapi" valor={v.org_facturapi} />
      <Renglon ok={!!F.tiene_llave} titulo="Llave de timbrado"
        valor={F.tiene_llave ? (F.es_produccion ? "de PRODUCCIÓN" : "de pruebas") : ""} />

      {!v.org_facturapi && (
        <div style={{ marginTop: 14 }}>
          <button onClick={crearOrganizacion} disabled={!!ocupado || !F.rfc}
            style={{ ...btnPrimary, opacity: (ocupado || !F.rfc) ? 0.4 : 1 }}>
            {ocupado === "org" ? "Creando…" : "Crear su organización en Facturapi"}
          </button>
          <div style={{ fontSize: 11, color: "#666", marginTop: 8, lineHeight: 1.7 }}>
            {F.rfc
              ? "Crea la organización con sus datos fiscales y guarda su llave de PRUEBAS: " +
                "queda timbrando de mentiras el mismo día. La de producción es aparte."
              : "Antes hace falta su RFC, su régimen y su código postal — suben de la constancia."}
          </div>
        </div>
      )}

      <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #1e1e1e" }}>
        <Label>Poner una llave a mano</Label>
        <input type="password" value={llave} onChange={e => setLlave(e.target.value)}
          placeholder="sk_test_… o sk_live_…" autoComplete="off"
          style={{ ...inputStyle, fontFamily: "monospace", marginTop: 4 }} />
        <label style={{ display: "flex", gap: 9, alignItems: "center", margin: "10px 0",
                        fontSize: 12, color: esLive ? "#f87171" : "#999", cursor: "pointer" }}>
          <input type="checkbox" checked={esLive} onChange={e => setEsLive(e.target.checked)}
            style={{ width: 17, height: 17, accentColor: esLive ? "#f87171" : "#666" }} />
          Es de <b>PRODUCCIÓN</b> — timbra ante el SAT de verdad
        </label>
        <button disabled={!!ocupado || llave.trim().length < 20}
          onClick={() => correr("llave", async () => {
            await rotarLlaveFiscal(v.tenant_id, llave.trim(), esLive, v.org_facturapi || null);
            setLlave(""); setAviso("Llave guardada."); onCambio?.();
          })}
          style={{ ...btnSecondary, padding: "8px 16px", fontSize: 12,
                   opacity: (ocupado || llave.trim().length < 20) ? 0.4 : 1 }}>
          {ocupado === "llave" ? "Guardando…" : "Guardar llave"}
        </button>
        <div style={{ fontSize: 11, color: "#666", marginTop: 10, lineHeight: 1.7 }}>
          La llave se guarda cifrada y <b style={{ color: "#888" }}>no se puede volver a
          leer</b>. Si dice ser de producción y no lo es, se rechaza: la forma tiene
          que coincidir con lo declarado.
          <br />
          El <b style={{ color: "#888" }}>sello del SAT</b> (.cer y .key) todavía se
          carga en el panel de Facturapi. Sin él se timbra en pruebas, que es lo que
          hace falta para trabajar y demostrar.
        </div>
      </div>
    </div>
  );
}

// ── Datos y plan ───────────────────────────────────────────────────────────
function SeccionDatos({ v, planes, correr, ocupado, rpc, setAviso, onCambio }) {
  const [nombre, setNombre] = useState(v.nombre);
  const [serie, setSerie] = useState("");
  // Vacío = hereda del plan. Se arranca vacío incluso cuando hay ajuste,
  // porque el campo enseña el tope del plan como marca de agua y así se ve de
  // un vistazo qué le tocaría sin negociación.
  const [asientos, setAsientos] = useState("");
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <Label>Nombre</Label>
          <input value={nombre} onChange={e => setNombre(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <Label>Serie de folios <span style={{ color: "#555" }}>· dejar vacío para no cambiarla</span></Label>
          <input value={serie} onChange={e => setSerie(e.target.value.toUpperCase())}
            maxLength={6} style={inputStyle} />
        </div>
      </div>
      <button disabled={!!ocupado} onClick={() => correr("datos", () =>
          rpc("actualizar_vinicola", { p_tenant: v.tenant_id, p_nombre: nombre, p_serie: serie || null },
              () => setAviso("Datos actualizados.")))}
        style={{ ...btnSecondary, marginTop: 12, padding: "8px 16px", fontSize: 12 }}>
        {ocupado === "datos" ? "Guardando…" : "Guardar datos"}
      </button>

      <div style={{ marginTop: 20, paddingTop: 14, borderTop: "1px solid #1e1e1e" }}>
        <Label>Plan · {v.asientos_usados} de {v.asientos_tope ?? "sin tope"} asientos usados</Label>
        <div style={{ display: "grid", gap: 10, marginTop: 8,
                      gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
          {planes.map(p => (
            <button key={p.code} disabled={!!ocupado || p.code === v.plan}
              onClick={() => correr("plan", () =>
                rpc("cambiar_plan_vinicola", { p_tenant: v.tenant_id, p_plan: p.code },
                    (d) => setAviso(`Plan: ${d.antes} → ${d.ahora}`)))}
              style={{ textAlign: "left", padding: "10px 12px", borderRadius: 8,
                       cursor: p.code === v.plan ? "default" : "pointer", fontFamily: "inherit",
                       background: p.code === v.plan ? "#1a1810" : "#141414",
                       border: `1px solid ${p.code === v.plan ? "#3a3222" : "#1e1e1e"}` }}>
              <div style={{ fontSize: 12, fontWeight: 600,
                            color: p.code === v.plan ? "#c9a96e" : "#aaa" }}>
                {p.label}{p.code === v.plan ? " · actual" : ""}
              </div>
              <div style={{ fontSize: 10, color: "#666" }}>
                {p.max_seats == null ? "sin tope de personas" : `hasta ${p.max_seats}`}
              </div>
              {Object.entries(p.features || {}).filter(([, x]) => x).length > 0 && (
                <div style={{ fontSize: 10, color: "#8aa070", marginTop: 4 }}>
                  {Object.entries(p.features).filter(([, x]) => x).length} capacidades
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Asientos negociados ─────────────────────────────────────────────
          El operador enumeró «crear dueños, administrar el número de asientos
          y el tipo de suscripción» como TRES cosas, y hasta 0062 eran dos: el
          número salía del plan y no se podía mover. Para vender «básico más
          dos» había que inventar un plan por cliente.

          Vacío = hereda del plan, que es el caso normal. Solo quien negoció
          algo distinto lleva número propio. */}
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid #1a1a1a" }}>
        <Label>Asientos de esta vinícola</Label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input type="number" min={1} value={asientos}
            placeholder={`${v.asientos_tope ?? "sin tope"} (del plan)`}
            onChange={e => setAsientos(e.target.value)}
            style={{ ...inputStyle, width: 120 }} />
          <button disabled={!!ocupado}
            onClick={() => correr("asientos", () =>
              rpc("ajustar_asientos_vinicola",
                  { p_tenant: v.tenant_id,
                    p_asientos: asientos === "" ? null : Number(asientos) },
                  onCambio))}
            style={btnSecondary}>
            {asientos === "" ? "Heredar del plan" : "Ajustar"}
          </button>
          <span style={{ fontSize: 11, color: "#666" }}>
            {v.asientos_usados} en uso
          </span>
        </div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 6, lineHeight: 1.6 }}>
          Déjalo vacío para que siga los asientos de su plan. Bajarlo por debajo
          de los que ya usa se rechaza: habría membresías vivas por encima del
          tope y el número diría una cosa mientras la realidad dice otra.
        </div>
      </div>
    </div>
  );
}

// ── Baja ───────────────────────────────────────────────────────────────────
function SeccionBaja({ v, correr, ocupado, rpc, setAviso }) {
  const [motivo, setMotivo] = useState("");
  if (!v.activa) {
    return (
      <div>
        <div style={{ fontSize: 12, color: "#888", lineHeight: 1.8, marginBottom: 12 }}>
          Está dada de baja. Su gente no puede entrar y sus datos siguen intactos.
        </div>
        <button disabled={!!ocupado}
          onClick={() => correr("react", () =>
            rpc("reactivar_vinicola", { p_tenant: v.tenant_id },
                () => setAviso("Reactivada. Su equipo puede volver a entrar.")))}
          style={btnPrimary}>
          {ocupado === "react" ? "Reactivando…" : "Reactivar"}
        </button>
      </div>
    );
  }
  return (
    <div>
      <div style={{ padding: "12px 14px", background: "#1f0d0d", border: "1px solid #3a1a1a",
                    borderRadius: 8, fontSize: 12, color: "#f87171", lineHeight: 1.7,
                    marginBottom: 14 }}>
        Su equipo deja de poder entrar. <b>No se borra nada</b>: los CFDI que timbró
        existen ante el SAT y sus remisiones son el respaldo de esas facturas. Se
        puede reactivar.
      </div>
      <Label>Por qué</Label>
      <input value={motivo} onChange={e => setMotivo(e.target.value)}
        placeholder="dejó de pagar, cerró, se pasó a otro sistema…" style={inputStyle} />
      <button disabled={!!ocupado || !motivo.trim()}
        onClick={() => correr("baja", async () => {
          if (!confirm(`Dar de baja «${v.nombre}».\n\nSu equipo deja de entrar. ` +
                       `Nada se borra y se puede reactivar.\n\n¿Seguro?`)) return;
          await rpc("suspender_vinicola", { p_tenant: v.tenant_id, p_motivo: motivo },
                    (d) => setAviso(`Dada de baja. Conserva ${d.cfdi_vigentes} CFDI vigentes.`));
        })}
        style={{ ...btnPrimary, marginTop: 12,
                 background: "linear-gradient(135deg, #3a1a1a, #2a0d0d)",
                 borderColor: "#5a2a2a", color: "#f87171",
                 opacity: (ocupado || !motivo.trim()) ? 0.4 : 1 }}>
        {ocupado === "baja" ? "Dando de baja…" : "Dar de baja"}
      </button>
    </div>
  );
}

function Renglon({ ok, titulo, valor }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
      <span style={{ color: ok ? "#4ade80" : "#facc15", width: 14 }}>{ok ? "✓" : "!"}</span>
      <span style={{ color: "#666", minWidth: 178 }}>{titulo}</span>
      <span style={{ color: ok ? "#e8e0d4" : "#facc15", fontSize: 12,
                     wordBreak: "break-all" }}>{valor || "falta"}</span>
    </div>
  );
}

function Aviso({ tono, children }) {
  const c = tono === "mal"
    ? { f: "#1f0d0d", b: "#3a1a1a", t: "#f87171" }
    : { f: "#0d1f0d", b: "#1a3a1a", t: "#4ade80" };
  return (
    <div style={{ padding: "10px 12px", background: c.f, border: `1px solid ${c.b}`,
                  borderRadius: 8, fontSize: 12, color: c.t, marginBottom: 12 }}>
      {children}
    </div>
  );
}
